import React from 'react'
import {
  LineItemDTO,
  usePrice,
  useBasketLineItem,
  useGlobalLabels,
  useConfigState,
} from '@sentecacommerce-theme/lib'
import styled from 'styled-components'
import { LazyResponsiveImage, Price, Typography } from '../../../Common'

export const ProductPreview = ({ item }: { item: LineItemDTO }) => {
  const {
    name,
    images,
    totalPrice,
    sku,
    quantityInBasket,
    totalDiscountedPrice,
    mappedSelectedOptions,
    getCustomFieldByKey,
    getAttributeByKey,
  } = useBasketLineItem({
    item,
  })
  const config = useConfigState()
  const selectedSize = getAttributeByKey(config.attributeKeys.size)
  const imageIndex = mappedSelectedOptions?.[0]?.value?.imageIndex || 0

  const labels = useGlobalLabels()

  const hideRegularPrice = getCustomFieldByKey?.(
    config?.customFieldKeys?.hideRegularPrice
  )

  const getPrice = () => {
    if (!hideRegularPrice) return totalPrice

    if (totalDiscountedPrice?.centAmount) return totalDiscountedPrice

    return totalPrice
  }

  const { price, discountPrice, discountPercentage } = usePrice({
    price: getPrice(),
    discountedPrice: !hideRegularPrice ? totalDiscountedPrice : undefined,
  })

  return (
    <Product>
      <Shadow>
        <ImageContainer>
          <LazyResponsiveImage
            breakpointSizes={{}}
            defaultImageWidth="16rem"
            imagesMap={images?.[imageIndex]}
            alt={name}
          />
        </ImageContainer>
        <ContentContainer>
          <h5>{name}</h5>
          {mappedSelectedOptions?.map((option) => (
            <OptionContainer
              key={`Option__${option.label}--${option.value?.key}`}
            >
              <Typography variant="8" className="mr-2">
                {option.label}:
              </Typography>
              <BoldText>{option.value?.label}</BoldText>
            </OptionContainer>
          ))}
          {selectedSize ? (
            <OptionContainer>
              <Typography variant="8" className="mr-2">
                {selectedSize?.label?.toLowerCase()}:
              </Typography>
              <BoldText>{selectedSize?.value}</BoldText>
            </OptionContainer>
          ) : null}
          <SKU>{`${labels?.productNumber}: ${sku}`}</SKU>
          <Quantity>{`${labels?.qty}: ${quantityInBasket}`}</Quantity>
        </ContentContainer>
        <PriceContainer>
          <Price
            price={price.value}
            size="small"
            hideDiscountPercentage
            discountedPrice={discountPrice ? discountPrice.value : undefined}
            currency={price.currency}
            hideRegularPrice={hideRegularPrice}
          />
        </PriceContainer>
      </Shadow>
    </Product>
  )
}

const Product = styled.li.attrs({
  className: 'col-12',
})``

const Shadow = styled.div.attrs({
  className: 'p-4 d-flex',
})`
  border-bottom: 0.1rem solid ${({ theme }) => theme.palette.lightgrey};
`

const ContentContainer = styled.div`
  & > h5 {
    font-weight: normal;
  }
`

const ImageContainer = styled.div.attrs({
  className: 'mr-3',
})`
  min-width: 16rem;
  min-width: 16rem;
`

const PriceContainer = styled.div.attrs({
  className: 'd-flex ml-auto justify-content-end align-items-start',
})`
  min-width: fit-content;
`
const SKU = styled(Typography).attrs({
  as: 'span',
  variant: 7,
  className: 'd-block mt-1 mb-3',
})`
  color: ${({ theme }) => theme.palette.greyTitle};
`

const Quantity = styled(Typography).attrs({
  as: 'span',
  variant: 6,
  className: 'd-block',
})``

const OptionContainer = styled.div.attrs({
  className: 'd-flex align-items-center my-2',
})`
  color: ${({ theme }) => theme.palette.greyTitle};
`
const BoldText = styled(Typography).attrs({ variant: '8' })`
  font-weight: 450;
`
